import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { QueryContextSettingID } from 'src/app/services/query-context/models/IQueryContext';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import { generateTableFullPath } from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { XYChartAxis } from 'src/app/utils/echarts/xy-chart-axis';
import { DataLoaderResult } from '../../../services/data-loader/data-loader-result';
import { standardRun } from '../../../services/data-loader/utils/standard-run';
import { BiVizAdsComparisonSqlMap } from '../sql-maps/biviz-ads-comparison-sql-map';

export interface AdsComparisonDataLoaderFactoryOptions
    extends ICreatedInstanceId, IGcpProjectId, IQueryCacheService {}

export class AdsComparisonDataLoaderFactory extends Factory<
    DataLoader,
    AdsComparisonDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string = 'AdsAnalyticsVsStoreDataLoaderFactory';
    /** This function returns DataLoader instance for analytics vs store reports differences */
    getInstance(options: AdsComparisonDataLoaderFactoryOptions) {
        // let options = verifyRequiredOptions(rawOptions);
        // if (!verifyRequiredOptions(options)) {
        //     throw Error(
        //         '[getSalesAnalyticsVsStoreFactory] Provided options are of type SalesAnalyticsVsStoreDataLoaderOptions'
        //     );
        // }
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                const datasourceColumns = queryContext.datasourceColumns;

                // identify columns not supported in datasource
                const dsColumns = DataLoader.separatePresentAndMissingDims(
                    datasourceColumns,
                    BiVizAdsComparisonSqlMap.sqlMap.columns
                );

                const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                    DataLoader.getMissingColumnsStatus(dsColumns.missing);

                const { splitSelect, splitGroupBy, where, dateTrunc } =
                    BiVizAdsComparisonSqlMap.compileSQLStatements(
                        dsColumns.present,
                        queryContext.settings,
                        options
                    );

                const query = `
    -- DataLoaderId: ${this.instanceId}

SELECT ${dateTrunc} AS date,
  ${splitSelect}

  -- impressions
  COALESCE(SUM(ads.impressions), 0) - COALESCE(SUM(report_ads.impressions), 0) AS impressions_cnt_abs_diff,

  -- revenue
  COALESCE(SUM(ads.revenue_pln), 0) - COALESCE(SUM(report_ads.revenue_pln), 0) AS revenue_pln_abs_diff,
  COALESCE(SUM(ads.revenue_usd), 0) - COALESCE(SUM(report_ads.revenue_usd), 0) AS revenue_usd_abs_diff,
FROM \`${generateTableFullPath(
                    options.gcpProjectId,
                    BiVizAdsComparisonSqlMap.sqlMap.dataset,
                    BiVizAdsComparisonSqlMap.sqlMap.tableId
                )}\`
${where}
GROUP BY ${splitGroupBy}date
ORDER BY ${splitGroupBy}date
    `;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();

                var data = [...queryResponse.result];
                bq2echartsformat(data);
                XYChartAxis.fillMissingTimeGapRows(
                    data,
                    queryContext,
                    'date',
                    queryContext.settings[
                        QueryContextSettingID.DATE_AGGREGATION
                    ] ?? 'day'
                );

                this.emitter.emit({
                    data,
                    queryContext,
                    status: pendingDataLoaderResultStatus,
                    statusMessage: pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
